import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Check, ShoppingBag, Briefcase, Camera,
  Utensils, Heart, Image, Type, Video, Mail, MessageSquareQuote,
  CalendarDays, Globe, Users, Layout, Minus, ClipboardList, Sparkles,
  X as XIcon, Maximize2, ArrowRight, ArrowLeft, Smartphone, Monitor
} from 'lucide-react';
import { SafeImg } from './SafeImg';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Stock photos per template category
const STOCK = {
  artisan: {
    hero: 'https://images.unsplash.com/photo-1624791469834-9a0f742f78e5?w=800&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1729383450683-865f6a6eaf9c?w=400&q=80',
      'https://images.pexels.com/photos/12333729/pexels-photo-12333729.jpeg?auto=compress&w=400',
      'https://images.pexels.com/photos/36597037/pexels-photo-36597037.jpeg?auto=compress&w=400',
    ],
  },
  service: {
    hero: 'https://images.unsplash.com/photo-1714974528232-f62051870736?w=800&q=80',
    about: 'https://images.unsplash.com/photo-1769839271768-aee5469799ee?w=400&q=80',
  },
  creative: {
    hero: 'https://images.unsplash.com/photo-1758613654757-7c4f113a6782?w=800&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1758613653298-738e98658e31?w=400&q=80',
    ],
  },
  food: {
    hero: 'https://images.unsplash.com/photo-1684568519316-f0fb02f7826b?w=800&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1684568519320-8c6b14f7e65f?w=400&q=80',
      'https://images.pexels.com/photos/6879452/pexels-photo-6879452.jpeg?auto=compress&w=400',
      'https://images.pexels.com/photos/33474124/pexels-photo-33474124.jpeg?auto=compress&w=400',
    ],
  },
  community: {
    hero: 'https://images.unsplash.com/photo-1760992003987-efc5259bcfbf?w=800&q=80',
    about: 'https://images.unsplash.com/photo-1765018028697-2baae4577cdd?w=400&q=80',
  },
  // ── New templates ──
  editorial: {
    hero: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&q=80',
    about: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&q=80',
  },
  reseda: {
    hero: 'https://images.unsplash.com/photo-1502136969935-8d8eef54d77b?w=1200&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1494522855154-9297ac14b55f?w=400&q=80',
      'https://images.unsplash.com/photo-1502720433255-614171a1835e?w=400&q=80',
      'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400&q=80',
    ],
  },
  bloom: {
    hero: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&q=80',
    about: 'https://images.unsplash.com/photo-1573497620053-ea5300f94f21?w=600&q=80',
    banner: 'https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=1200&q=80',
  },
  showroom: {
    hero: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1200&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400&q=80',
      'https://images.unsplash.com/photo-1521577352947-9bb58764b69a?w=400&q=80',
      'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=400&q=80',
      'https://images.unsplash.com/photo-1539109136881-3be0616acf4b?w=400&q=80',
    ],
  },
  beat: {
    hero: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1200&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=400&q=80',
      'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=400&q=80',
      'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=400&q=80',
    ],
  },
  // ── Round 2 of new templates (Bennie / Radian / Clarkson + beadwork + events) ──
  camp: {
    hero: 'https://images.unsplash.com/photo-1517825738774-7de9363ef735?w=1200&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1551966775-a4ddc8df052b?w=400&q=80',
      'https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?w=400&q=80',
      'https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=400&q=80',
      'https://images.unsplash.com/photo-1521543387207-c2c43e6ba7c8?w=400&q=80',
    ],
  },
  builder: {
    hero: 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=1200&q=80',
    project1: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=600&q=80',
    project2: 'https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=600&q=80',
    project3: 'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=600&q=80',
  },
  wild: {
    hero: 'https://images.unsplash.com/photo-1426604966848-d7adac402bff?w=1400&q=80',
    forest: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=1400&q=80',
    impact: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600&q=80',
  },
  beadwork: {
    hero: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=1200&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1602192103751-fdf9d2c20f51?w=400&q=80',
      'https://images.unsplash.com/photo-1607008829749-c0f284a49841?w=400&q=80',
      'https://images.unsplash.com/photo-1583292650898-7d22cd27ca6f?w=400&q=80',
      'https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=400&q=80',
    ],
  },
  events: {
    hero: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1400&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?w=400&q=80',
      'https://images.unsplash.com/photo-1531058020387-3be344556be6?w=400&q=80',
      'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&q=80',
    ],
  },
};

// ── Full-size preview block renderers (website-like) ──
const PH = ({ src, h = 200, children, overlay = true }) => (
  <div className="relative w-full overflow-hidden" style={{ height: h }}>
    <SafeImg src={src} alt="" className="w-full h-full object-cover" />
    {overlay && <div className="absolute inset-0 bg-black/40" />}
    {children && <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-8">{children}</div>}
  </div>
);

const FullHero = ({ title, tagline, buttons, img, dark }) => (
  <div className="relative overflow-hidden" style={{ minHeight: 300 }}>
    {img ? <SafeImg src={img} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className={`absolute inset-0 ${dark ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-black' : 'bg-gradient-to-br from-indigo-50 to-purple-100'}`} />}
    <div className="absolute inset-0 bg-black/50" />
    <div className="relative z-10 flex flex-col items-center justify-center text-center text-white px-8 py-20">
      <h1 className="text-3xl md:text-4xl font-bold leading-tight">{title}</h1>
      <p className="text-base opacity-80 mt-3 max-w-lg">{tagline}</p>
      <div className="flex gap-3 mt-6">
        {buttons?.map((b, i) => (
          <span key={i} className={`px-5 py-2 rounded-lg text-sm font-semibold ${i === 0 ? 'bg-[#8B5CF6] text-white' : 'border border-white/40 text-white'}`}>{b}</span>
        ))}
      </div>
    </div>
  </div>
);

// Fallback product photos used by FullProducts when a template doesn't pass
// its own images. Spread across categories so every template's preview shows
// real photos rather than gray boxes. Each URL has been verified — Picsum
// fallback is guaranteed never-404.
const FALLBACK_PRODUCT_IMAGES = [
  'https://picsum.photos/seed/wb-prod-1/400/500',
  'https://picsum.photos/seed/wb-prod-2/400/500',
  'https://picsum.photos/seed/wb-prod-3/400/500',
  'https://picsum.photos/seed/wb-prod-4/400/500',
  'https://picsum.photos/seed/wb-prod-5/400/500',
];

const FullProducts = ({ title, subtitle, count = 3, images = [], names = [], prices = [] }) => {
  const defaultNames = ['House Signature', 'The Studio Edit', 'Limited Release', 'The Heirloom', 'Maker\'s Pick'];
  const defaultPrices = ['From $48', 'From $124', 'From $260', 'From $89', 'From $172'];
  const safeImgs = images && images.length > 0 ? images : FALLBACK_PRODUCT_IMAGES;
  return (
    <div className="py-12 px-8 text-center bg-white">
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
      {subtitle && <p className="text-gray-500 mt-2">{subtitle}</p>}
      <div className="flex flex-wrap gap-6 justify-center mt-8">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="w-40 text-left">
            <SafeImg src={safeImgs[i % safeImgs.length]} alt="" className="w-40 h-48 object-cover rounded-lg" />
            <p className="mt-3 text-sm font-semibold text-gray-900 truncate">{names[i] || defaultNames[i % defaultNames.length]}</p>
            <p className="mt-1 text-xs text-gray-500">{prices[i] || defaultPrices[i % defaultPrices.length]}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const FullAbout = ({ title, text, img }) => (
  <div className="py-12 px-8 flex gap-8 items-center bg-gray-50">
    {img && <SafeImg src={img} alt="" className="w-48 h-56 object-cover rounded-xl shrink-0" />}
    <div>
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
      <p className="text-gray-500 mt-3 leading-relaxed">{text}</p>
    </div>
  </div>
);

const FullGallery = ({ title, images = [], layout = 'grid' }) => (
  <div className="py-10 px-8 bg-white">
    <h2 className="text-xl font-bold text-gray-900 text-center mb-6">{title}</h2>
    <div className={layout === 'lookbook' ? 'space-y-4 max-w-2xl mx-auto' : 'grid grid-cols-3 gap-3 max-w-3xl mx-auto'}>
      {images.length > 0 ? images.map((img, i) => (
        <div key={i} className={`overflow-hidden rounded-lg ${layout === 'lookbook' ? 'h-48' : 'aspect-square'}`}>
          <SafeImg src={img} alt="" className="w-full h-full object-cover" />
        </div>
      )) : Array.from({ length: layout === 'lookbook' ? 3 : 6 }).map((_, i) => (
        <div
          key={i}
          className={`rounded-lg ${layout === 'lookbook' ? 'h-48' : 'aspect-square'}`}
          style={{
            background: `linear-gradient(${135 + i*30}deg, hsl(${260 + i*15}, 60%, ${85 - i*3}%) 0%, hsl(${320 + i*10}, 70%, ${88 - i*3}%) 100%)`,
          }}
        />
      ))}
    </div>
  </div>
);

const FullTestimonial = ({ quote, author, role }) => (
  <div className="py-10 px-8 bg-gray-50 text-center">
    <p className="text-lg italic text-gray-700 max-w-xl mx-auto">"{quote}"</p>
    <p className="text-gray-900 font-bold mt-4">— {author}</p>
    {role && <p className="text-gray-500 text-sm">{role}</p>}
  </div>
);

const FullSignup = ({ title, subtitle }) => (
  <div className="py-10 px-8 bg-gray-900 text-center text-white">
    <h2 className="text-xl font-bold">{title}</h2>
    <p className="text-gray-400 mt-2">{subtitle}</p>
    <div className="flex gap-2 justify-center mt-5 max-w-md mx-auto">
      <div className="flex-1 h-10 bg-white/10 border border-white/20 rounded-lg" />
      <button className="px-5 h-10 bg-[#8B5CF6] rounded-lg text-sm font-bold">Subscribe</button>
    </div>
  </div>
);

const FullBooking = ({ title, subtitle }) => (
  <div className="py-10 px-8 bg-indigo-50 text-center">
    <h2 className="text-xl font-bold text-gray-900">{title}</h2>
    <p className="text-gray-500 mt-2">{subtitle}</p>
    <div className="flex gap-4 justify-center mt-6">
      {['Consultation', 'Full Session', 'Premium'].map(s => (
        <div key={s} className="px-6 py-4 border border-gray-200 rounded-xl bg-white text-center">
          <p className="font-bold text-gray-900">{s}</p>
          <p className="text-gray-400 text-sm mt-1">60 min</p>
        </div>
      ))}
    </div>
    <button className="mt-6 px-6 py-2.5 bg-[#8B5CF6] text-white rounded-lg font-bold">Book Now</button>
  </div>
);

const FullBanner = ({ text }) => (
  <div className="py-3 bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-center text-white text-sm font-medium">{text}</div>
);

const FullForm = ({ title, desc }) => (
  <div className="py-10 px-8 bg-gray-50">
    <div className="max-w-md mx-auto">
      <h2 className="text-xl font-bold text-gray-900 text-center">{title}</h2>
      <p className="text-gray-500 text-sm text-center mt-2">{desc}</p>
      <div className="space-y-3 mt-6">
        {['Full Name', 'Email Address', 'Message'].map(f => (
          <div key={f}><label className="text-xs font-medium text-gray-700">{f}</label><div className={`mt-1 bg-white border border-gray-200 rounded-lg ${f === 'Message' ? 'h-20' : 'h-9'}`} /></div>
        ))}
      </div>
      <button className="w-full mt-4 py-2.5 bg-[#8B5CF6] text-white rounded-lg font-bold">Submit</button>
    </div>
  </div>
);

const FullDonation = ({ title, subtitle }) => (
  <div className="py-10 px-8 bg-rose-50 text-center">
    <h2 className="text-xl font-bold text-gray-900">{title}</h2>
    <p className="text-gray-500 mt-2">{subtitle}</p>
    <div className="flex gap-3 justify-center mt-6">
      {['$25', '$50', '$100', '$250'].map(a => (
        <span key={a} className="px-5 py-2 border border-gray-300 rounded-lg bg-white font-bold text-gray-800">{a}</span>
      ))}
    </div>
    <button className="mt-5 px-6 py-2.5 bg-rose-500 text-white rounded-lg font-bold">Donate Now</button>
  </div>
);

const FullText = ({ title, text }) => (
  <div className="py-10 px-8 text-center bg-white">
    {title && <h2 className="text-xl font-bold text-gray-900">{title}</h2>}
    <p className="text-gray-500 mt-2 max-w-2xl mx-auto leading-relaxed">{text}</p>
  </div>
);

const FullVideo = () => (
  <div className="py-8 px-8 bg-white"><div className="max-w-2xl mx-auto h-52 bg-gray-900 rounded-xl flex items-center justify-center"><div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center"><div className="w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-l-[14px] border-l-white ml-1" /></div></div></div>
);

const FullEmbed = () => (
  <div className="py-8 px-8 bg-gray-50"><div className="max-w-2xl mx-auto h-48 rounded-xl flex items-center justify-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #EDE9FE 0%, #FCE7F3 50%, #FEF3C7 100%)' }}><div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 12px, rgba(139,92,246,0.08) 12px, rgba(139,92,246,0.08) 13px)' }} /><div className="relative flex flex-col items-center gap-2"><Globe className="w-10 h-10 text-violet-500/80" /><p className="text-xs font-semibold text-violet-700/80 tracking-wider uppercase">Map · Embed · Video</p></div></div></div>
);

const FullMarquee = ({ words = [], bg = '#0F172A', color = '#FFFFFF', size = 'text-2xl', sep = '•', italic = false }) => {
  const loop = [...words, ...words, ...words, ...words];
  return (
    <div className="relative overflow-hidden py-5" style={{ background: bg }}>
      <style>{`@keyframes wb-tpl-marquee { from {transform: translateX(0);} to {transform: translateX(-50%);} }`}</style>
      <div
        className={`flex whitespace-nowrap items-center gap-10 ${size} font-extrabold`}
        style={{ color, fontStyle: italic ? 'italic' : 'normal', letterSpacing: '0.04em', width: 'max-content', animation: 'wb-tpl-marquee 30s linear infinite' }}
      >
        {loop.map((w, i) => (
          <span key={i} className="inline-flex items-center gap-10 px-4">{w}{sep && <span aria-hidden style={{ opacity: 0.55 }}>{sep}</span>}</span>
        ))}
      </div>
    </div>
  );
};

// ── The 5 Templates ──
const TEMPLATES = [
  {
    id: 'indigenous-artisan',
    name: 'Indigenous Artisan Shop',
    category: 'E-Commerce',
    desc: 'Showcase handcrafted goods with a gallery, product grid, testimonials, and newsletter signup. Perfect for artists and makers.',
    accent: '#8B5CF6',
    blocks: ['hero', 'products', 'about', 'gallery', 'testimonial', 'signup'],
    renderMockup: () => (
      <div className="bg-white rounded-t-xl overflow-hidden">
        <div className="relative h-28 overflow-hidden"><SafeImg src={STOCK.artisan.hero} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white"><p className="font-bold text-[9px]">Handcrafted Indigenous Art</p><p className="text-[5px] opacity-80">Authentic creations rooted in tradition</p><div className="flex gap-1 mt-1"><span className="px-2 py-0.5 bg-[#8B5CF6] text-white text-[5px] rounded-sm">Shop</span><span className="px-2 py-0.5 border border-white/40 text-[5px] rounded-sm">Story</span></div></div></div>
        <div className="px-3 py-2 text-center"><p className="text-[7px] font-bold">Featured Creations</p><div className="flex gap-1 justify-center mt-1">{[0,1,2].map(i => <div key={i} className="w-10"><div className="w-10 h-12 rounded-sm overflow-hidden"><SafeImg src={STOCK.artisan.gallery[i % STOCK.artisan.gallery.length]} alt="" className="w-full h-full object-cover" /></div><div className="w-8 h-0.5 bg-gray-200 mt-1 mx-auto rounded" /></div>)}</div></div>
        <div className="px-3 py-2 flex gap-2"><div className="w-10 h-12 rounded-sm overflow-hidden shrink-0"><SafeImg src={STOCK.artisan.gallery[0]} alt="" className="w-full h-full object-cover" /></div><div><p className="text-[7px] font-bold">Our Heritage</p><div className="w-full h-0.5 bg-gray-200 rounded mt-1" /><div className="w-4/5 h-0.5 bg-gray-200 rounded mt-0.5" /></div></div>
        <div className="px-3 py-1.5"><p className="text-[6px] font-bold text-center mb-1">Gallery</p><div className="grid grid-cols-3 gap-0.5">{STOCK.artisan.gallery.map((img,i) => <div key={i} className="aspect-square rounded-sm overflow-hidden"><SafeImg src={img} alt="" className="w-full h-full object-cover" /></div>)}</div></div>
        <div className="px-3 py-1.5 bg-gray-50 text-center"><p className="text-[5px] italic text-gray-500">"Incredible craftsmanship"</p><p className="text-[5px] font-bold mt-0.5">— Customer</p></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <FullHero title="Handcrafted Indigenous Art" tagline="Authentic creations rooted in tradition, made for the modern world" buttons={['Shop Collection', 'Our Story']} img={STOCK.artisan.hero} />
        <FullProducts title="Featured Creations" subtitle="Each piece carries a story" />
        <FullAbout title="Our Heritage" text="We honor generations of Indigenous artistry. Every product is handmade using traditional techniques passed down through generations." img={STOCK.artisan.gallery[0]} />
        <FullGallery title="Gallery" images={STOCK.artisan.gallery} />
        <FullTestimonial quote="The craftsmanship is incredible — you can feel the tradition in every piece." author="Happy Customer" role="Verified Buyer" />
        <FullSignup title="Join Our Community" subtitle="Get first access to new collections and exclusive offers" />
      </>
    ),
  },
  {
    id: 'service-professional',
    name: 'Service Professional',
    category: 'Services',
    desc: 'Booking calendar, service list, quote form, and dual testimonials. Ideal for consultants, contractors, and freelancers.',
    accent: '#3B82F6',
    blocks: ['hero', 'custom_text', 'products', 'booking', 'testimonial', 'testimonial', 'form', 'signup'],
    renderMockup: () => (
      <div className="bg-white rounded-t-xl overflow-hidden">
        <div className="relative h-24 overflow-hidden"><SafeImg src={STOCK.service.hero} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white"><p className="font-bold text-[8px]">Professional Services</p><p className="text-[5px] opacity-80">Expert solutions for your needs</p><div className="flex gap-1 mt-1"><span className="px-2 py-0.5 bg-blue-500 text-white text-[5px] rounded-sm">Book Now</span></div></div></div>
        <div className="px-3 py-1.5 text-center"><p className="text-[7px] font-bold">Why Choose Us</p><div className="w-3/4 h-0.5 bg-gray-200 rounded mx-auto mt-0.5" /></div>
        <div className="px-3 py-2 text-center"><p className="text-[7px] font-bold">Our Services</p><div className="flex gap-1 justify-center mt-1">{['Consult','Deliver','Support'].map((lbl,i) => <div key={i} className="w-10"><div className="w-10 h-10 rounded-sm flex items-center justify-center text-[5px] font-bold text-white" style={{ background: `linear-gradient(135deg, #3B82F6 ${20+i*15}%, #8B5CF6 100%)` }}>{lbl}</div><div className="w-6 h-0.5 bg-gray-200 mt-1 mx-auto rounded" /></div>)}</div></div>
        <div className="px-3 py-1.5 bg-indigo-50 text-center"><p className="text-[6px] font-bold">Schedule a Consultation</p><div className="flex gap-1 justify-center mt-1">{[1,2,3].map(i => <div key={i} className="px-1.5 py-0.5 border border-gray-200 rounded-sm bg-white text-[4px]">Service {i}</div>)}</div></div>
        <div className="px-3 py-1 bg-gray-50 text-center"><p className="text-[5px] italic text-gray-500">"Absolutely professional"</p></div>
        <div className="px-3 py-1.5"><p className="text-[6px] font-bold text-center">Get a Free Quote</p><div className="space-y-0.5 mt-0.5"><div className="h-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-sm" /><div className="h-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-sm" /></div></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <FullHero title="Professional Services You Can Trust" tagline="Expert solutions tailored to your needs" buttons={['Book Now', 'View Services']} img={STOCK.service.hero} />
        <FullText title="Why Choose Us" text="With years of experience and a commitment to excellence, we deliver results that exceed expectations." />
        <FullProducts title="Our Services" subtitle="Explore our full range of professional offerings" />
        <FullBooking title="Schedule a Consultation" subtitle="Pick a time that works for you" />
        <FullTestimonial quote="Absolutely professional from start to finish. Highly recommended!" author="Satisfied Client" role="Business Owner" />
        <FullTestimonial quote="They went above and beyond. The best service I've ever received." author="Another Client" role="Repeat Customer" />
        <FullForm title="Get a Free Quote" desc="Tell us about your project and we'll get back to you within 24 hours" />
        <FullSignup title="Stay Updated" subtitle="Subscribe for tips, news, and special offers" />
      </>
    ),
  },
  {
    id: 'creative-portfolio',
    name: 'Creative Portfolio',
    category: 'Portfolio',
    desc: 'Lookbook gallery, behind-the-scenes video, artist bio, and commission form. Built for photographers, designers, and visual artists.',
    accent: '#EC4899',
    blocks: ['single_image', 'custom_text', 'gallery', 'spacer', 'about', 'video', 'testimonial', 'form'],
    renderMockup: () => (
      <div className="bg-white rounded-t-xl overflow-hidden">
        <div className="h-16 overflow-hidden"><SafeImg src={STOCK.creative.hero} alt="" className="w-full h-full object-cover" /></div>
        <div className="px-3 py-1.5 text-center"><p className="text-[8px] font-bold">Creative Portfolio</p><div className="w-3/4 h-0.5 bg-gray-200 rounded mx-auto mt-0.5" /></div>
        <div className="px-3 py-1 space-y-1">{STOCK.creative.gallery.map((img,i) => <div key={i} className="h-10 rounded-sm overflow-hidden"><SafeImg src={img} alt="" className="w-full h-full object-cover" /></div>)}<div className="h-10 rounded-sm bg-gradient-to-br from-violet-100 via-pink-50 to-amber-50 flex items-center justify-center"><span className="text-[6px] font-bold text-violet-500/70 tracking-wide">MORE WORKS</span></div><div className="h-10 rounded-sm bg-gradient-to-br from-pink-100 via-violet-50 to-blue-50 flex items-center justify-center"><span className="text-[6px] font-bold text-violet-500/70 tracking-wide">SERIES 02</span></div></div>
        <div className="h-2" />
        <div className="px-3 py-1.5 flex gap-2"><div className="w-10 h-10 rounded-sm overflow-hidden shrink-0"><SafeImg src={STOCK.creative.gallery[0]} alt="" className="w-full h-full object-cover" /></div><div><p className="text-[7px] font-bold">About the Artist</p><div className="w-full h-0.5 bg-gray-200 rounded mt-0.5" /></div></div>
        <div className="px-3 py-1.5"><div className="h-10 bg-gray-900 rounded-sm flex items-center justify-center"><div className="w-3 h-3 rounded-full bg-white/20 flex items-center justify-center"><div className="w-0 h-0 border-t-[2px] border-t-transparent border-b-[2px] border-b-transparent border-l-[3px] border-l-white ml-0.5" /></div></div></div>
        <div className="px-3 py-1 bg-gray-50 text-center"><p className="text-[5px] italic text-gray-500">"Transformative work"</p></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <div className="py-8 bg-white"><SafeImg src={STOCK.creative.hero} alt="" className="max-h-64 mx-auto rounded-xl object-cover" /></div>
        <FullText title="Creative Portfolio" text="Visual storytelling through art, photography, and design." />
        <FullGallery title="Selected Works" images={[...STOCK.creative.gallery, ...STOCK.creative.gallery, ...STOCK.creative.gallery].slice(0,6)} layout="lookbook" />
        <div className="h-12 bg-white" />
        <FullAbout title="About the Artist" text="Blending Indigenous heritage with contemporary expression, creating works that bridge past and present." />
        <FullVideo />
        <FullTestimonial quote="Their work is transformative — it captures something words can't." author="Art Collector" role="Gallery Owner" />
        <FullForm title="Commission Inquiry" desc="Interested in a custom piece? Let's talk." />
      </>
    ),
  },
  {
    id: 'food-restaurant',
    name: 'Food & Restaurant',
    category: 'Food & Beverage',
    desc: 'Menu display, photo gallery, table reservations, location embed, and promo banner. Made for restaurants and food businesses.',
    accent: '#F59E0B',
    blocks: ['hero', 'banner', 'products', 'gallery', 'booking', 'about', 'embed', 'signup'],
    renderMockup: () => (
      <div className="bg-white rounded-t-xl overflow-hidden">
        <div className="relative h-24 overflow-hidden"><SafeImg src={STOCK.food.hero} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white"><p className="font-bold text-[8px]">Taste the Tradition</p><p className="text-[5px] opacity-80">Indigenous-inspired cuisine</p><div className="flex gap-1 mt-1"><span className="px-2 py-0.5 bg-amber-500 text-black text-[5px] rounded-sm font-bold">Menu</span><span className="px-2 py-0.5 border border-white/40 text-[5px] rounded-sm">Reserve</span></div></div></div>
        <div className="py-1 bg-gradient-to-r from-amber-500/30 to-orange-500/30 text-center"><p className="text-[5px] font-medium">Now accepting catering orders!</p></div>
        <div className="px-3 py-2 text-center"><p className="text-[7px] font-bold">Our Menu</p><div className="flex gap-1 justify-center mt-1">{[0,1,2,3].map(i => <div key={i} className="w-8"><div className="w-8 h-10 rounded-sm overflow-hidden"><SafeImg src={STOCK.food.gallery[i % STOCK.food.gallery.length]} alt="" className="w-full h-full object-cover" /></div></div>)}</div></div>
        <div className="px-3 py-1"><div className="grid grid-cols-3 gap-0.5">{STOCK.food.gallery.map((img,i) => <div key={i} className="aspect-square rounded-sm overflow-hidden"><SafeImg src={img} alt="" className="w-full h-full object-cover" /></div>)}</div></div>
        <div className="px-3 py-1 bg-amber-50 text-center"><p className="text-[6px] font-bold">Reserve Your Table</p></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <FullHero title="Taste the Tradition" tagline="Indigenous-inspired cuisine crafted with locally sourced ingredients" buttons={['View Menu', 'Reserve a Table']} img={STOCK.food.hero} dark />
        <FullBanner text="Now accepting catering orders for events!" />
        <FullProducts title="Our Menu" subtitle="Fresh, seasonal dishes inspired by Indigenous food traditions" count={4} />
        <FullGallery title="From Our Kitchen" images={STOCK.food.gallery} />
        <FullBooking title="Reserve Your Table" subtitle="Book online — walk-ins also welcome" />
        <FullAbout title="Our Story" text="Rooted in Indigenous culinary traditions, we bring ancestral flavors to the modern table. Every dish tells a story of land, culture, and community." img={STOCK.food.gallery[0]} />
        <FullEmbed />
        <FullSignup title="Get Exclusive Offers" subtitle="Join our mailing list for specials and event invitations" />
      </>
    ),
  },
  {
    id: 'community-nonprofit',
    name: 'Community & Nonprofit',
    category: 'Nonprofit',
    desc: 'Donation widget, impact stats, volunteer form, photo gallery, and community testimonials. For organizations and community groups.',
    accent: '#10B981',
    blocks: ['hero', 'about', 'custom_text', 'donations', 'gallery', 'testimonial', 'form', 'signup'],
    renderMockup: () => (
      <div className="bg-white rounded-t-xl overflow-hidden">
        <div className="relative h-24 overflow-hidden"><SafeImg src={STOCK.community.hero} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white"><p className="font-bold text-[8px]">Building Stronger Communities</p><p className="text-[5px] opacity-80">Education, advocacy, and action</p><div className="flex gap-1 mt-1"><span className="px-2 py-0.5 bg-emerald-500 text-white text-[5px] rounded-sm">Donate</span><span className="px-2 py-0.5 border border-white/40 text-[5px] rounded-sm">Learn More</span></div></div></div>
        <div className="px-3 py-1.5 flex gap-2"><div className="w-10 h-10 rounded-sm overflow-hidden shrink-0"><SafeImg src={STOCK.community.about} alt="" className="w-full h-full object-cover" /></div><div><p className="text-[7px] font-bold">Our Mission</p><div className="w-full h-0.5 bg-gray-200 rounded mt-0.5" /><div className="w-3/4 h-0.5 bg-gray-200 rounded mt-0.5" /></div></div>
        <div className="px-3 py-1 text-center"><p className="text-[7px] font-bold">Our Impact</p><p className="text-[5px] text-gray-500">500+ families • 20+ programs • 10 years</p></div>
        <div className="px-3 py-1.5 bg-rose-50 text-center"><p className="text-[6px] font-bold">Support Our Work</p><div className="flex gap-1 justify-center mt-0.5">{['$25','$50','$100'].map(a => <span key={a} className="px-1.5 py-0.5 text-[5px] border border-gray-300 rounded-sm bg-white">{a}</span>)}</div></div>
        <div className="px-3 py-1 bg-gray-50 text-center"><p className="text-[5px] italic text-gray-500">"Essential and deeply impactful"</p></div>
        <div className="px-3 py-1"><p className="text-[6px] font-bold text-center">Get Involved</p><div className="h-2 rounded-sm mt-0.5 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100" /></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <FullHero title="Building Stronger Communities" tagline="Supporting Indigenous communities through education, advocacy, and action" buttons={['Donate Now', 'Learn More']} img={STOCK.community.hero} dark />
        <FullAbout title="Our Mission" text="We are dedicated to empowering Indigenous communities through education, cultural preservation, and economic opportunity." img={STOCK.community.about} />
        <FullText title="Our Impact" text="500+ families supported. 20+ community programs. 10 years of dedicated service. Your support makes this possible." />
        <FullDonation title="Support Our Work" subtitle="Every contribution directly impacts Indigenous families and communities" />
        <FullGallery title="Community Moments" />
        <FullTestimonial quote="This organization changed our community. Their work is essential and deeply impactful." author="Community Elder" role="Tribal Council Member" />
        <FullForm title="Get Involved" desc="Volunteer, partner, or just learn more about our programs" />
        <FullSignup title="Stay Connected" subtitle="Monthly newsletter with stories, updates, and upcoming events" />
      </>
    ),
  },

  // ─────────────────────────────────────────────────────────────
  // NEW TEMPLATES (inspired by Brixen / Reseda / Bloom + Indigenous use-cases)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'editorial-coach',
    name: 'Editorial Coach',
    category: 'Coaching · Public Speaking',
    desc: 'Deep editorial palette with a moving testimonial ticker. Built for coaches, public speakers, educators, and consultants.',
    accent: '#1A4D2E',
    blocks: ['hero', 'marquee', 'custom_text', 'products', 'about', 'booking', 'testimonial', 'form', 'signup'],
    renderMockup: () => (
      <div className="bg-[#F5F1E9] rounded-t-xl overflow-hidden">
        <div className="relative h-24 overflow-hidden"><SafeImg src={STOCK.editorial.hero} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-[#1A4D2E]/70 flex flex-col items-center justify-center text-white"><p className="font-bold text-[8px]">Precision strategies for ambitious futures.</p><span className="px-2 py-0.5 bg-[#F5F1E9] text-[#1A4D2E] text-[5px] rounded-sm font-bold mt-1">Book a Consultation</span></div></div>
        <div className="h-3 bg-[#1A4D2E] flex items-center overflow-hidden"><p className="text-[5px] text-[#F5F1E9] font-bold px-2 truncate">★ Indigenous-led ★ Trusted by 500+ ★ Featured nationwide ★</p></div>
        <div className="px-3 py-1.5 text-center"><p className="text-[7px] font-bold text-[#1A4D2E]">Our service</p><div className="w-3/4 h-0.5 bg-[#1A4D2E]/20 rounded mx-auto mt-0.5" /></div>
        <div className="px-3 py-1.5 text-center"><p className="text-[7px] font-bold text-[#1A4D2E]">Programs</p><div className="flex gap-1 justify-center mt-0.5">{['1:1 Coaching','Group','Keynotes'].map((lbl,i) => <div key={i} className="w-10 h-10 border border-[#1A4D2E]/25 rounded-sm flex items-center justify-center text-[4.5px] font-bold text-[#1A4D2E] text-center px-0.5 leading-tight" style={{ background: `linear-gradient(135deg, rgba(26,77,46,0.08) 0%, rgba(245,241,233,1) 100%)` }}>{lbl}</div>)}</div></div>
        <div className="px-3 py-1.5 bg-[#1A4D2E] text-[#F5F1E9] text-center"><p className="text-[6px] font-bold">Top 1%</p><p className="text-[5px] opacity-80">Outcomes & retention</p></div>
        <div className="px-3 py-1 bg-[#F5F1E9] text-center"><p className="text-[5px] italic text-[#1A4D2E]">"Strategy, mentorship, results."</p></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <FullHero title="Precision strategies for ambitious futures." tagline="One-on-one mentorship, group programs, and resources built around your goals." buttons={['Book a Consultation', 'View Programs']} img={STOCK.editorial.hero} dark />
        <FullMarquee words={['Indigenous-led', 'Trusted by 500+ clients', 'Featured nationwide', 'Strategy. Mentorship. Results.']} bg="#1A4D2E" color="#F5F1E9" size="text-xl" sep="★" />
        <FullText title="Our service" text="We help you get the tools, strategy, and confidence to lead — not just compete." />
        <FullProducts title="Programs & coaching" subtitle="Three tracks designed around how you learn best" />
        <FullAbout title="A curriculum built around your potential." text="Every client gets a personalised pathway, paired with a mentor who has lived experience in your field." img={STOCK.editorial.about} />
        <FullBooking title="Schedule a free consultation" subtitle="30 minutes — no pressure, no obligation." />
        <FullTestimonial quote="I came in unsure of my voice. I left with a strategy, a network, and the confidence to lead." author="Alyssa M." role="Founder, Northern Wave Studio" />
        <FullForm title="Let's begin the conversation." desc="Tell us a little about you and we'll be in touch within 1 business day." />
        <FullSignup title="Sign up to our newsletter" subtitle="News, resources, and event invitations — once a month." />
      </>
    ),
  },
  {
    id: 'reseda-studio',
    name: 'Reseda Studio',
    category: 'Photography · Film',
    desc: 'Massive editorial typography with a slow scrolling category line. For photographers, filmmakers, and visual artists who want images to speak first.',
    accent: '#0A0A0A',
    blocks: ['single_image', 'custom_text', 'marquee', 'about', 'gallery', 'gallery', 'gallery', 'form', 'signup'],
    renderMockup: () => (
      <div className="bg-[#F8F8F8] rounded-t-xl overflow-hidden">
        <div className="h-20 overflow-hidden"><SafeImg src={STOCK.reseda.hero} alt="" className="w-full h-full object-cover" /></div>
        <div className="px-3 py-1.5"><p className="text-[10px] font-black text-[#0A0A0A] tracking-tight">RESEDA</p><p className="text-[5px] text-gray-500 mt-0.5">LA-based photography</p></div>
        <div className="h-3 bg-black flex items-center overflow-hidden"><p className="text-[5px] text-[#F8F8F8] font-bold px-2 truncate tracking-widest">EDITORIAL / FASHION / FINE ART / COMMISSIONED</p></div>
        <div className="px-3 py-1.5 flex gap-1.5"><div className="w-10 h-12 rounded-sm overflow-hidden shrink-0"><SafeImg src={STOCK.reseda.gallery[0]} alt="" className="w-full h-full object-cover" /></div><div><p className="text-[7px] font-bold text-[#0A0A0A]">Photography services</p><div className="w-full h-0.5 bg-gray-200 rounded mt-0.5" /></div></div>
        <div className="px-3 py-1"><p className="text-[6px] font-bold text-[#0A0A0A]">Winter Collection</p><div className="space-y-0.5 mt-0.5">{STOCK.reseda.gallery.map((img,i) => <div key={i} className="h-6 rounded-sm overflow-hidden"><SafeImg src={img} alt="" className="w-full h-full object-cover" /></div>)}</div></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <div className="bg-[#F8F8F8] py-8 px-8"><SafeImg src={STOCK.reseda.hero} alt="" className="w-full max-h-96 object-cover rounded-xl" /></div>
        <div className="bg-[#F8F8F8] px-10 py-6"><h1 className="text-7xl font-black text-[#0A0A0A] tracking-tight">RESEDA</h1><p className="text-gray-500 mt-3 max-w-md">LA-based photography. Editorial, fashion, fine art.</p></div>
        <FullMarquee words={['EDITORIAL', 'FASHION', 'FINE ART', 'COMMISSIONED WORK', 'STILL LIFE']} bg="#0A0A0A" color="#F8F8F8" size="text-3xl" sep="/" />
        <FullAbout title="Photography services" text="From editorial campaigns to intimate portraits. Each session is built around your story — soft light, real moments, beautifully crafted." img={STOCK.reseda.gallery[0]} />
        <FullGallery title="Winter Collection" images={STOCK.reseda.gallery} layout="lookbook" />
        <FullGallery title="Fall Collection" images={STOCK.reseda.gallery.slice(0,2)} />
        <FullForm title="Book a session" desc="Tell me a little about your project — I respond within 24h." />
        <FullSignup title="Subscribe" subtitle="New work, behind-the-scenes, and limited prints — straight to your inbox." />
      </>
    ),
  },
  {
    id: 'bloom-wellness',
    name: 'Bloom Wellness',
    category: 'Coaching · Wellness',
    desc: 'Warm beige & golden-yellow palette. Soft lifestyle imagery, membership tiers, and a sunny marquee. Perfect for wellness coaches, healers, and consultants.',
    accent: '#FBC02D',
    blocks: ['hero', 'about', 'banner', 'custom_text', 'products', 'marquee', 'testimonial', 'testimonial', 'booking', 'signup'],
    renderMockup: () => (
      <div className="bg-[#F5F2ED] rounded-t-xl overflow-hidden">
        <div className="relative h-24 overflow-hidden"><SafeImg src={STOCK.bloom.hero} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center text-white"><p className="font-bold text-[8px]">Take control of your business.</p><span className="px-2 py-0.5 bg-[#FBC02D] text-black text-[5px] rounded-sm font-bold mt-1">Join Now</span></div></div>
        <div className="px-3 py-1.5 flex gap-1.5"><div className="w-10 h-10 rounded-sm overflow-hidden shrink-0"><SafeImg src={STOCK.bloom.about} alt="" className="w-full h-full object-cover" /></div><div><p className="text-[7px] font-bold text-[#212121]">We're here to help you thrive.</p><div className="w-full h-0.5 bg-gray-300 rounded mt-0.5" /></div></div>
        <div className="h-10 overflow-hidden relative"><SafeImg src={STOCK.bloom.banner} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/30 flex items-center justify-center"><p className="text-[6px] font-bold text-white">Overcome your obstacles</p></div></div>
        <div className="px-3 py-1.5 text-center"><p className="text-[7px] font-bold text-[#212121]">Choose your starting point</p></div>
        <div className="px-3 py-1.5 flex gap-1 justify-center">{['Free', '$30/mo', '$240/yr'].map((p,i) => <div key={i} className="flex-1 px-1 py-1.5 bg-white border border-gray-200 rounded-sm text-center"><p className="text-[5px] font-bold text-[#212121]">Tier {i+1}</p><p className="text-[5px] text-[#FBC02D] font-bold">{p}</p></div>)}</div>
        <div className="h-3 bg-[#FBC02D] flex items-center overflow-hidden"><p className="text-[5px] font-bold px-2 truncate text-black">PLAN • BUILD • LAUNCH • GROW • THRIVE</p></div>
        <div className="px-3 py-1 bg-[#F5F2ED] text-center"><p className="text-[5px] italic text-gray-600">"The most clarity I've had about my business in years."</p></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <FullHero title="Take control of your business." tagline="Personalised business coaching for Indigenous founders and creatives." buttons={['Join Now']} img={STOCK.bloom.hero} />
        <FullAbout title="We're here to help you thrive." text="Our coaches help individuals and teams turn business dreams into reality — with strategy, confidence, and the right support at every step." img={STOCK.bloom.about} />
        <div className="relative h-48 overflow-hidden"><SafeImg src={STOCK.bloom.banner} alt="" className="absolute inset-0 w-full h-full object-cover" /><div className="absolute inset-0 bg-black/40 flex items-center justify-center"><p className="text-2xl text-white font-bold">Overcome your obstacles — one milestone at a time.</p></div></div>
        <FullText title="Choose your starting point" text="Explore memberships & class packs. Begin with a free resource or jump into the full programme." />
        <FullProducts title="Memberships" subtitle="Beginner's guide • Monthly • Annual" />
        <FullMarquee words={['Plan', 'Build', 'Launch', 'Grow', 'Thrive']} bg="#FBC02D" color="#1A1A1A" size="text-2xl" sep="•" />
        <FullTestimonial quote="The most clarity I've had about my business in years. Worth every dollar." author="Shirley L." role="Small business owner" />
        <FullTestimonial quote="Practical, warm, and full of strategy I could actually use the next day." author="Maya R." role="Founder, Two Rivers Press" />
        <FullBooking title="Let's kickstart your business." subtitle="We'll be with you every step of the way." />
        <FullSignup title="Stay inspired" subtitle="Tips, stories, and news — once a fortnight." />
      </>
    ),
  },
  {
    id: 'marquee-showroom',
    name: 'Marquee Showroom',
    category: 'Fashion · Apparel',
    desc: 'Bold black & gold typography with marquee tickers top and middle. Built for fashion designers, models, and limited-drop apparel brands.',
    accent: '#FBBF24',
    blocks: ['marquee', 'hero', 'products', 'gallery', 'marquee', 'about', 'video', 'testimonial', 'signup'],
    renderMockup: () => (
      <div className="bg-black rounded-t-xl overflow-hidden">
        <div className="h-4 flex items-center overflow-hidden"><p className="text-[5px] font-bold px-2 truncate text-[#FBBF24] tracking-widest">★ NEW DROP ★ INDIGENOUS DESIGN ★ LIMITED RUN ★ TURTLE ISLAND MADE ★</p></div>
        <div className="relative h-24 overflow-hidden"><SafeImg src={STOCK.showroom.hero} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white"><p className="font-black text-[9px] tracking-widest">ATELIER —— FW26</p><div className="flex gap-1 mt-1"><span className="px-2 py-0.5 bg-[#FBBF24] text-black text-[5px] rounded-sm font-bold">Shop the Drop</span><span className="px-2 py-0.5 border border-white/40 text-white text-[5px] rounded-sm">Lookbook</span></div></div></div>
        <div className="px-3 py-1.5 text-center bg-white"><p className="text-[7px] font-bold text-black">The Collection</p><div className="flex gap-1 justify-center mt-1">{[0,1,2,3].map(i => <div key={i} className="w-8 h-10 rounded-sm overflow-hidden"><SafeImg src={STOCK.showroom.gallery[i % STOCK.showroom.gallery.length]} alt="" className="w-full h-full object-cover" /></div>)}</div></div>
        <div className="px-3 py-1 bg-white"><div className="grid grid-cols-2 gap-0.5">{STOCK.showroom.gallery.slice(0,4).map((img,i) => <div key={i} className="aspect-square rounded-sm overflow-hidden"><SafeImg src={img} alt="" className="w-full h-full object-cover" /></div>)}</div></div>
        <div className="h-3 bg-[#FBBF24] flex items-center overflow-hidden"><p className="text-[5px] font-bold px-2 truncate text-black">FREE SHIPPING OVER $150 / ETHICALLY MADE / EVERY PIECE TELLS A STORY</p></div>
        <div className="px-3 py-1 bg-white text-center"><p className="text-[5px] italic text-gray-500">"Quality I haven't seen since vintage."</p></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <FullMarquee words={['NEW DROP', 'INDIGENOUS DESIGN', 'LIMITED RUN', 'TURTLE ISLAND MADE']} bg="#000000" color="#FBBF24" size="text-3xl" sep="★" />
        <FullHero title="ATELIER —— FW26" tagline="Modern silhouettes rooted in tradition. Made in small batches on Turtle Island." buttons={['Shop the Drop', 'Lookbook']} img={STOCK.showroom.hero} dark />
        <FullProducts title="The Collection" subtitle="Hand-finished. Numbered. Built to outlast trends." count={4} />
        <FullGallery title="Lookbook" images={STOCK.showroom.gallery} />
        <FullMarquee words={['FREE SHIPPING OVER $150', 'ETHICALLY MADE', 'EVERY PIECE TELLS A STORY']} bg="#FBBF24" color="#000000" size="text-xl" sep="/" />
        <FullAbout title="Behind the brand" text="Designed by Indigenous makers. Cut in small batches. Beadwork and finishings done by hand in our studio — never outsourced." img={STOCK.showroom.gallery[0]} />
        <FullVideo />
        <FullTestimonial quote="Quality I haven't seen since vintage — and a story I'm proud to wear." author="Brianna T." role="Loyal customer since 2024" />
        <FullSignup title="First access to the next drop" subtitle="Members get early access 24h before public release." />
      </>
    ),
  },
  {
    id: 'beat-atelier',
    name: 'Beat Atelier',
    category: 'Music · DJ',
    desc: 'Dark, video-first layout with a fast neon marquee for tour announcements. Built for music artists, DJs, producers, and live performers.',
    accent: '#7C3AED',
    blocks: ['single_image', 'custom_text', 'marquee', 'video', 'embed', 'products', 'booking', 'gallery', 'form', 'signup'],
    renderMockup: () => (
      <div className="bg-[#0A0A14] rounded-t-xl overflow-hidden">
        <div className="h-20 overflow-hidden"><SafeImg src={STOCK.beat.hero} alt="" className="w-full h-full object-cover" /></div>
        <div className="px-3 py-1.5 text-center"><p className="text-[10px] font-black text-white tracking-widest">RAVENWOLF</p><p className="text-[5px] text-gray-400 mt-0.5">Producer · DJ · Live performance</p></div>
        <div className="h-3 bg-[#7C3AED] flex items-center overflow-hidden"><p className="text-[5px] font-bold px-2 truncate text-white">NEW SINGLE OUT NOW • LIVE @ MOTH CLUB MAR 14 • TOUR DATES BELOW</p></div>
        <div className="px-3 py-1.5"><div className="h-10 bg-black rounded-sm flex items-center justify-center"><div className="w-4 h-4 rounded-full bg-[#7C3AED]/40 flex items-center justify-center"><div className="w-0 h-0 border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent border-l-[4px] border-l-white ml-0.5" /></div></div></div>
        <div className="px-3 py-1.5"><p className="text-[7px] font-bold text-white">Merch & vinyl</p><div className="flex gap-1 mt-1">{['LP','TEE','CAP'].map((lbl,i) => <div key={i} className="w-8 h-10 rounded-sm flex items-center justify-center text-[6px] font-black text-white/90" style={{ background: `linear-gradient(135deg, #7C3AED ${i*20}%, #EC4899 100%)` }}>{lbl}</div>)}</div></div>
        <div className="px-3 py-1"><div className="grid grid-cols-3 gap-0.5">{STOCK.beat.gallery.map((img,i) => <div key={i} className="aspect-square rounded-sm overflow-hidden"><SafeImg src={img} alt="" className="w-full h-full object-cover" /></div>)}</div></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <div className="bg-[#0A0A14] py-8 px-8"><SafeImg src={STOCK.beat.hero} alt="" className="w-full max-h-80 object-cover rounded-xl" /></div>
        <div className="bg-[#0A0A14] text-center px-8 py-6"><h1 className="text-5xl font-black text-white tracking-widest">RAVENWOLF</h1><p className="text-gray-400 mt-2">Producer · DJ · Live performance — Indigenous electronic music from the Pacific Northwest.</p></div>
        <FullMarquee words={['NEW SINGLE OUT NOW', 'LIVE @ MOTH CLUB · MAR 14', 'TOUR DATES BELOW', 'BOOK FOR YOUR VENUE']} bg="#7C3AED" color="#FFFFFF" size="text-xl" sep="•" />
        <FullVideo />
        <FullEmbed />
        <FullProducts title="Merch & vinyl" subtitle="Limited runs — once they're gone, they're gone." />
        <FullBooking title="Book a live show" subtitle="Festivals, clubs, brand activations — get a quote in 24h." />
        <FullGallery title="On stage" images={STOCK.beat.gallery} />
        <FullForm title="Booking enquiry" desc="Festival, club, or private event? Send us the details." />
        <FullSignup title="Join the mailing list" subtitle="Tour dates, new music, and pre-sale access." />
      </>
    ),
  },

  // ─────────────────────────────────────────────────────────────
  // ROUND 2 (Bennie / Radian / Clarkson inspired + Beadwork + Events)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'camp-spirit',
    name: 'Camp Spirit',
    category: 'Youth · Workshops',
    desc: 'Bright red/yellow/blue color blocking with a playful marquee. Built for youth summer camps, cultural workshops, and kids\u2019 arts programs.',
    accent: '#E51C23',
    blocks: ['hero', 'marquee', 'about', 'products', 'gallery', 'testimonial', 'form', 'signup'],
    renderMockup: () => (
      <div className="bg-white rounded-t-xl overflow-hidden">
        <div className="relative h-24 overflow-hidden"><SafeImg src={STOCK.camp.hero} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center text-white"><p className="font-black text-[9px]">Camp Wáakam</p><p className="text-[5px] opacity-90">Where Creativity Runs Free</p><span className="px-2 py-0.5 bg-[#E51C23] text-white text-[5px] rounded-sm font-bold mt-1">Register Now</span></div></div>
        <div className="h-3 bg-[#FFDA47] flex items-center overflow-hidden"><p className="text-[5px] font-bold px-2 truncate text-black">★ ARTS ★ NATURE ★ STORYTELLING ★ DRUMMING ★</p></div>
        <div className="px-3 py-1.5 flex gap-1.5"><div className="w-10 h-10 rounded-sm overflow-hidden shrink-0"><SafeImg src={STOCK.camp.gallery[0]} alt="" className="w-full h-full object-cover" /></div><div><p className="text-[7px] font-bold">About Camp</p><div className="w-full h-0.5 bg-gray-200 rounded mt-0.5" /></div></div>
        <div className="px-3 py-1.5 bg-[#FFDA47]/40 text-center"><p className="text-[7px] font-bold">Programs</p><div className="flex gap-1 justify-center mt-1">{['Day','Night','Wknd'].map((lbl,i) => <div key={i} className="w-10 h-10 rounded-sm flex items-center justify-center text-[5.5px] font-black" style={{ background: `linear-gradient(135deg, #FFF8DA ${i*15}%, #FFDA47 100%)`, color: '#78350F' }}>{lbl}</div>)}</div></div>
        <div className="px-3 py-1 bg-[#1C3A9E]/10"><div className="grid grid-cols-3 gap-0.5">{STOCK.camp.gallery.slice(0,3).map((img,i) => <div key={i} className="aspect-square rounded-sm overflow-hidden"><SafeImg src={img} alt="" className="w-full h-full object-cover" /></div>)}</div></div>
        <div className="px-3 py-1.5 bg-[#E51C23] text-center"><p className="text-[6px] font-bold text-white">Request Information</p></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <FullHero title="Camp Wáakam" tagline="Where Creativity Runs Free — Indigenous arts, nature & storytelling for ages 6\u201314." buttons={['Register Now', 'About Us']} img={STOCK.camp.hero} />
        <FullMarquee words={['ARTS', 'NATURE', 'STORYTELLING', 'DRUMMING', 'BEADWORK', 'OUTDOOR PLAY']} bg="#FFDA47" color="#1A1A1A" size="text-2xl" sep="★" />
        <FullAbout title="A summer of wonder, rooted in culture." text="Camp Wáakam is a 6-week summer immersion that blends Indigenous knowledge, outdoor adventure, and creative arts. Every camper goes home with skills, friendships, and stories that last a lifetime." img={STOCK.camp.gallery[0]} />
        <FullProducts title="Programs" subtitle="Day camps, overnight sessions, and weekend workshops." count={3} />
        <FullGallery title="Camp Life" images={STOCK.camp.gallery} />
        <FullTestimonial quote="My daughter came home glowing. She learned beadwork, language, and how to make a fire." author="Tasha B." role="Camp Parent" />
        <FullForm title="Request Information" desc="Tell us about your camper and we\u2019ll get you the full schedule and pricing." />
        <FullSignup title="For Families" subtitle="Monthly updates and early registration." />
      </>
    ),
  },
  {
    id: 'builder-studio',
    name: 'Builder Studio',
    category: 'Construction · Architecture',
    desc: 'Minimalist editorial portfolio with a sophisticated marquee and detailed inquiry form. For construction companies, architects, and Indigenous-led developers.',
    accent: '#0A0A0A',
    blocks: ['hero', 'custom_text', 'marquee', 'products', 'gallery', 'about', 'testimonial', 'form', 'signup'],
    renderMockup: () => (
      <div className="bg-white rounded-t-xl overflow-hidden">
        <div className="relative h-20 overflow-hidden"><SafeImg src={STOCK.builder.hero} alt="" className="w-full h-full object-cover" /></div>
        <div className="px-3 py-2"><p className="text-[8px] font-black text-[#0A0A0A] tracking-tight">RADIAN builds future-facing spaces for highly creative humans.</p><p className="text-[5px] text-gray-500 mt-0.5">A \u2018haste not, want not\u2019 approach to structures that embrace our Earth\u2019s drifting conditions.</p></div>
        <div className="h-3 bg-black flex items-center overflow-hidden"><p className="text-[5px] text-white font-bold px-2 truncate tracking-widest">SUSTAINABLE DESIGN / HYBRID SPACES / RENOVATION + RESTORATION</p></div>
        <div className="px-3 py-1.5"><p className="text-[6px] font-bold uppercase tracking-widest text-[#0A0A0A]">Projects</p><div className="grid grid-cols-3 gap-1 mt-1">{[STOCK.builder.project1, STOCK.builder.project2, STOCK.builder.project3].map((img,i) => <div key={i} className="aspect-square rounded-sm overflow-hidden"><SafeImg src={img} alt="" className="w-full h-full object-cover" /></div>)}</div></div>
        <div className="px-3 py-1.5 bg-gray-50"><p className="text-[6px] font-bold">About</p><div className="grid grid-cols-3 gap-1 mt-0.5 text-[5px] text-gray-500"><div>Services</div><div>Clients</div><div>Locations</div></div></div>
        <div className="px-3 py-1.5 bg-[#0A0A0A] text-center"><p className="text-[6px] font-bold text-white">Let\u2019s Build!</p></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <FullHero title="Radian builds future-facing spaces for highly creative humans." tagline="A \u2018haste not, want not\u2019 approach to structures that embrace our Earth\u2019s drifting conditions." buttons={['Start a Project', 'View Work']} img={STOCK.builder.hero} dark />
        <FullText title="" text="We\u2019re an Indigenous-led design and build studio working across new construction, renovation, and landscape architecture \u2014 with sustainability and community at the centre of every decision." />
        <FullMarquee words={['SUSTAINABLE DESIGN', 'HYBRID SPACES', 'RENOVATION + RESTORATION', 'LANDSCAPE ARCHITECTURE']} bg="#0A0A0A" color="#FFFFFF" size="text-2xl" sep="/" />
        <FullProducts title="Projects" subtitle="A curated selection of recent builds and design partnerships." />
        <FullGallery title="Explore" images={[STOCK.builder.project1, STOCK.builder.project2, STOCK.builder.project3]} layout="lookbook" />
        <FullAbout title="About" text="Services \u00b7 Sustainable Design, Hybrid Spaces, Renovation + Restoration. Clients \u00b7 Tribal Councils, Squarespace, Tesla. Locations \u00b7 New York, Los Angeles, Vancouver." img={STOCK.builder.project1} />
        <FullTestimonial quote="They delivered an elegant building on time and on budget. The detail, craftsmanship, and respect for the land were unmatched." author="Director, Northern Lights Cultural Center" role="Past Client" />
        <FullForm title="Let\u2019s Build!" desc="Tell us about your project. We respond to every enquiry within 2 business days." />
        <FullSignup title="Studio Notes" subtitle="A quarterly letter from the studio \u2014 design thinking, finished projects, and field notes." />
      </>
    ),
  },
  {
    id: 'wild-lands',
    name: 'Wild Lands',
    category: 'Nonprofit · Conservation',
    desc: 'Cinematic landscape hero with mission-driven copy and a calm motto marquee. Built for tribal organizations, land defenders, and conservation nonprofits.',
    accent: '#1A4D2E',
    blocks: ['hero', 'custom_text', 'about', 'marquee', 'banner', 'donations', 'testimonial', 'signup', 'form'],
    renderMockup: () => (
      <div className="bg-white rounded-t-xl overflow-hidden">
        <div className="relative h-24 overflow-hidden"><SafeImg src={STOCK.wild.hero} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white"><p className="font-bold text-[9px]">Protecting natural habitats from extinction</p><span className="px-2 py-0.5 bg-white text-[#1A4D2E] text-[5px] rounded-sm font-bold mt-1">Learn More</span></div></div>
        <div className="px-3 py-1.5 bg-gray-50"><p className="text-[7px] font-bold text-[#1A4D2E]">Climate change threatens every part of the planet.</p><p className="text-[5px] text-gray-500 mt-0.5">It\u2019s a global problem that requires global cooperation.</p></div>
        <div className="px-3 py-1.5 flex gap-1.5"><div className="w-10 h-10 rounded-sm overflow-hidden shrink-0"><SafeImg src={STOCK.wild.impact} alt="" className="w-full h-full object-cover" /></div><div><p className="text-[7px] font-bold">Changing the world is possible</p><div className="w-full h-0.5 bg-gray-200 rounded mt-0.5" /></div></div>
        <div className="h-3 bg-[#1A4D2E] flex items-center overflow-hidden"><p className="text-[5px] font-bold px-2 truncate text-white">\u2014 LAND BACK \u2014 PROTECT THE WATER \u2014 SEVENTH GENERATION \u2014</p></div>
        <div className="px-3 py-1.5 bg-[#FFDA47]/30 text-center"><p className="text-[6px] font-bold">Ready to take the next step?</p><span className="px-2 py-0.5 bg-[#1A4D2E] text-white text-[5px] rounded-sm font-bold mt-0.5 inline-block">Take Action</span></div>
        <div className="px-3 py-1 text-center"><p className="text-[5px] italic text-gray-500">"Sign up for updates from the field."</p></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <FullHero title="Protecting natural habitats from extinction." tagline="An Indigenous-led conservation alliance defending the lands and waters of Turtle Island." buttons={['Take Action', 'Learn More']} img={STOCK.wild.hero} dark />
        <FullText title="Climate change threatens every part of the planet." text="It\u2019s a global problem that requires global cooperation \u2014 led by the communities who have stewarded these lands since time immemorial." />
        <FullAbout title="Changing the world is possible. We\u2019ve done it before." text="Our coalition unites 23 tribal nations, scientists, and community leaders working to restore ecosystems, defend treaty rights, and advance climate justice." img={STOCK.wild.impact} />
        <FullMarquee words={['LAND BACK', 'PROTECT THE WATER', 'SEVENTH GENERATION', 'TREATY RIGHTS', 'CLIMATE JUSTICE']} bg="#1A4D2E" color="#FFFFFF" size="text-xl" sep="—" />
        <div className="relative h-56 overflow-hidden"><SafeImg src={STOCK.wild.forest} alt="" className="absolute inset-0 w-full h-full object-cover" /><div className="absolute inset-0 bg-black/45 flex items-center justify-center px-8 text-center"><p className="text-2xl text-white italic max-w-xl">"The land does not belong to us \u2014 we belong to the land."</p></div></div>
        <FullDonation title="Ready to take the next step?" subtitle="Every contribution funds direct action: legal defence, land restoration, and youth programming." />
        <FullTestimonial quote="The work this coalition does is not just policy \u2014 it is medicine for the land and for our people." author="Elder, Nipissing First Nation" role="Council Member" />
        <FullSignup title="Let\u2019s meet up in real life" subtitle="Field-trip invitations, community gatherings, and updates from the front lines." />
        <FullForm title="Get involved" desc="Volunteer, partner with us, or tell us how you want to help." />
      </>
    ),
  },
  {
    id: 'maker-studio',
    name: 'Maker Studio',
    category: 'Beadwork · Craft',
    desc: 'Warm earthy palette with a hand-lettered technique marquee. Designed for beadworkers, quillworkers, weavers, and traditional craft artisans.',
    accent: '#8B4513',
    blocks: ['hero', 'marquee', 'about', 'gallery', 'products', 'video', 'testimonial', 'form', 'signup'],
    renderMockup: () => (
      <div className="bg-[#FAF5EF] rounded-t-xl overflow-hidden">
        <div className="relative h-24 overflow-hidden"><SafeImg src={STOCK.beadwork.hero} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/45 flex flex-col items-center justify-center text-white"><p className="font-bold text-[9px]" style={{ fontFamily: 'serif' }}>Stitched by hand. Rooted in story.</p><span className="px-2 py-0.5 bg-[#8B4513] text-white text-[5px] rounded-sm font-bold mt-1">Shop the Collection</span></div></div>
        <div className="h-3 bg-[#8B4513] flex items-center overflow-hidden"><p className="text-[5px] font-bold px-2 truncate text-[#FAF5EF] italic">beadwork \u2022 quillwork \u2022 hide tanning \u2022 weaving \u2022 ribbon work</p></div>
        <div className="px-3 py-1.5 flex gap-1.5"><div className="w-10 h-10 rounded-sm overflow-hidden shrink-0"><SafeImg src={STOCK.beadwork.gallery[0]} alt="" className="w-full h-full object-cover" /></div><div><p className="text-[7px] font-bold text-[#3F2A1A]">The Maker</p><div className="w-full h-0.5 bg-[#3F2A1A]/20 rounded mt-0.5" /></div></div>
        <div className="px-3 py-1"><p className="text-[6px] font-bold text-[#3F2A1A] text-center mb-0.5">Recent Pieces</p><div className="grid grid-cols-3 gap-0.5">{STOCK.beadwork.gallery.slice(0,3).map((img,i) => <div key={i} className="aspect-square rounded-sm overflow-hidden"><SafeImg src={img} alt="" className="w-full h-full object-cover" /></div>)}</div></div>
        <div className="px-3 py-1.5 bg-[#FAF5EF] text-center"><p className="text-[7px] font-bold text-[#3F2A1A]">Commission a piece</p></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <FullHero title="Stitched by hand. Rooted in story." tagline="Original beadwork, quillwork & ribbon work \u2014 made one piece at a time by an Indigenous artisan." buttons={['Shop the Collection', 'Commission a piece']} img={STOCK.beadwork.hero} dark />
        <FullMarquee words={['beadwork', 'quillwork', 'hide tanning', 'weaving', 'ribbon work', 'medallions']} bg="#8B4513" color="#FAF5EF" size="text-xl" sep="•" italic />
        <FullAbout title="The Maker" text="I am a citizen of the Anishinaabe Nation. I learned this work from my Nokomis, who learned from her Nokomis. Every piece I make carries that lineage \u2014 and a little of you." img={STOCK.beadwork.gallery[0]} />
        <FullGallery title="Recent Pieces" images={STOCK.beadwork.gallery} />
        <FullProducts title="Available Now" subtitle="Earrings, medallions, and one-of-a-kind heirlooms." />
        <FullVideo />
        <FullTestimonial quote="My earrings arrived in a hand-stitched bag with a card naming the colours. Every detail told me this was made with love." author="Mara P." role="Verified buyer" />
        <FullForm title="Commission a piece" desc="Tell me what you\u2019re imagining \u2014 colour, size, occasion. Custom orders take 4\u20136 weeks." />
        <FullSignup title="Studio drops" subtitle="New work goes to subscribers first \u2014 1 email per drop, never spam." />
      </>
    ),
  },
  {
    id: 'gathering-events',
    name: 'Gathering & Events',
    category: 'Events · Pow Wow · Festivals',
    desc: 'Bold festival-style layout with a date-and-lineup marquee. For pow wows, tribal festivals, cultural gatherings, and community event organizers.',
    accent: '#DC2626',
    blocks: ['hero', 'marquee', 'custom_text', 'gallery', 'products', 'booking', 'testimonial', 'form', 'signup'],
    renderMockup: () => (
      <div className="bg-[#0F0A06] rounded-t-xl overflow-hidden">
        <div className="relative h-24 overflow-hidden"><SafeImg src={STOCK.events.hero} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center text-white"><p className="font-black text-[9px] tracking-widest">SPIRIT OF THE NORTH</p><p className="text-[5px] opacity-90">3-Day Pow Wow \u00b7 Aug 16\u201318</p><span className="px-2 py-0.5 bg-[#DC2626] text-white text-[5px] rounded-sm font-bold mt-1">Get Tickets</span></div></div>
        <div className="h-3 bg-[#DC2626] flex items-center overflow-hidden"><p className="text-[5px] font-bold px-2 truncate text-white tracking-widest">FRI AUG 16 \u2022 SAT AUG 17 \u2022 SUN AUG 18 \u2022 GATES 11 AM \u2022 GRAND ENTRY 1 PM</p></div>
        <div className="px-3 py-1.5 text-center"><p className="text-[7px] font-bold text-[#FAF5EF]">3 days. 80+ dancers. 1 unforgettable weekend.</p></div>
        <div className="px-3 py-1"><p className="text-[6px] font-bold text-center text-[#FAF5EF] mb-0.5">Festival moments</p><div className="grid grid-cols-3 gap-0.5">{STOCK.events.gallery.map((img,i) => <div key={i} className="aspect-square rounded-sm overflow-hidden"><SafeImg src={img} alt="" className="w-full h-full object-cover" /></div>)}</div></div>
        <div className="px-3 py-1.5 bg-[#DC2626]/20 text-center"><p className="text-[6px] font-bold text-[#FAF5EF]">Reserve a vendor booth</p></div>
      </div>
    ),
    renderFullPreview: () => (
      <>
        <FullHero title="SPIRIT OF THE NORTH" tagline="3-Day Pow Wow \u00b7 August 16\u201318 \u00b7 80+ dancers, drum groups & Indigenous vendors." buttons={['Get Tickets', 'Vendor Booth']} img={STOCK.events.hero} dark />
        <FullMarquee words={['FRI AUG 16', 'SAT AUG 17', 'SUN AUG 18', 'GATES 11 AM', 'GRAND ENTRY 1 PM']} bg="#DC2626" color="#FFFFFF" size="text-2xl" sep="•" />
        <FullText title="3 days. 80+ dancers. 1 unforgettable weekend." text="Spirit of the North is an open, family-friendly gathering celebrating Indigenous song, dance, food, and craft. All nations welcome." />
        <FullGallery title="From past gatherings" images={STOCK.events.gallery} />
        <FullProducts title="Tickets" subtitle="Day passes, weekend passes & VIP." />
        <FullBooking title="Reserve a vendor booth" subtitle="80+ booths fill fast \u2014 we prioritise Indigenous-owned businesses." />
        <FullTestimonial quote="The most welcoming festival I\u2019ve ever attended. We\u2019re bringing the whole family back next year." author="Sienna C." role="2024 Attendee" />
        <FullForm title="Press & partnerships" desc="Media accreditation, sponsorship and partnership enquiries." />
        <FullSignup title="Be first to know" subtitle="Line-up announcements, early-bird ticket access, and after-festival recaps." />
      </>
    ),
  },
];

const blockLabelMap = {
  hero: 'Hero Section', banner: 'Banner', products: 'Products', about: 'About',
  gallery: 'Gallery', signup: 'Signup', custom_text: 'Text', video: 'Video',
  testimonial: 'Testimonial', spacer: 'Spacer', booking: 'Booking',
  form: 'Form', embed: 'Embed', single_image: 'Image', donations: 'Donations',
  marquee: 'Marquee',
};
const blockIconMap = {
  hero: Layout, banner: Image, products: ShoppingBag, about: Users, gallery: Image,
  signup: Mail, custom_text: Type, video: Video, testimonial: MessageSquareQuote,
  spacer: Minus, booking: CalendarDays, form: ClipboardList, embed: Globe,
  single_image: Image, donations: Heart, marquee: Sparkles,
};

export default function DashboardTemplates() {
  const navigate = useNavigate();
  const [applying, setApplying] = useState(null);
  const [applied, setApplied] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  // Switch-template confirmation flow.
  // Stage 1: pickMode — user picks smart-merge vs fresh-start
  // Stage 2: finalConfirm — irreversible-action confirmation
  const [switchTpl, setSwitchTpl] = useState(null); // template being switched to
  const [switchStage, setSwitchStage] = useState(null); // 'pickMode' | 'finalConfirm'
  const [switchMode, setSwitchMode] = useState('merge'); // 'merge' | 'fresh'
  const [currentBlocks, setCurrentBlocks] = useState(0); // # of current home blocks

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Master-authored custom templates merged into the vendor gallery. The
  // master CRUDs them in /master/templates and they appear here instantly
  // on next page load.
  const [customTemplates, setCustomTemplates] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/builder/custom-templates`, { headers })
      .then(r => { if (!cancelled) setCustomTemplates(r.data?.templates || []); })
      .catch(() => { /* non-fatal — built-in templates still render */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openSwitchDialog = async (template) => {
    setSwitchTpl(template);
    setSwitchStage('pickMode');
    setSwitchMode('merge');
    // Detect whether the user has existing content (so we can hide the merge
    // option entirely for first-time picks). One quick GET is cheap.
    try {
      const r = await axios.get(`${API}/builder/content`, { headers });
      setCurrentBlocks((r.data?.blocks || []).length);
    } catch {
      setCurrentBlocks(0);
    }
  };

  const applyTemplate = async (templateId, mode = 'fresh') => {
    setApplying(templateId);
    setSwitchStage(null);
    try {
      const res = await axios.post(`${API}/builder/apply-template`, { template_id: templateId, mode }, { headers });
      setApplied(templateId);
      toast.success(
        mode === 'merge'
          ? `Template applied with smart-merge — your content was kept across ${res.data.blocks_count} blocks.`
          : `Fresh-start applied — ${res.data.blocks_count} new blocks loaded.`
      );
      setTimeout(() => navigate('/website-builder/content'), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply template');
    } finally { setApplying(null); }
  };

  // Iter 189h — user-customisable glow color for template-card hover animation.
  // Default is null → each card uses its own template.accent so glows feel
  // contextual to the template. When the user picks a global colour it
  // overrides every card. Persisted in localStorage.
  const GLOW_PRESETS = [
    { id: 'auto',    label: 'Auto (per template)', color: null,      swatch: 'linear-gradient(135deg, #8B5CF6, #EC4899, #F59E0B, #10B981)' },
    { id: 'violet',  label: 'Violet',              color: '#8B5CF6', swatch: '#8B5CF6' },
    { id: 'pink',    label: 'Pink',                color: '#EC4899', swatch: '#EC4899' },
    { id: 'blue',    label: 'Blue',                color: '#3B82F6', swatch: '#3B82F6' },
    { id: 'emerald', label: 'Emerald',             color: '#10B981', swatch: '#10B981' },
    { id: 'amber',   label: 'Amber',               color: '#F59E0B', swatch: '#F59E0B' },
    { id: 'crimson', label: 'Crimson',             color: '#DC2626', swatch: '#DC2626' },
  ];
  const [glowChoice, setGlowChoice] = useState(() => {
    try { return localStorage.getItem('wb_template_glow') || 'auto'; } catch { return 'auto'; }
  });
  const [customGlow, setCustomGlow] = useState(() => {
    try { return localStorage.getItem('wb_template_glow_custom') || '#A78BFA'; } catch { return '#A78BFA'; }
  });
  const activeGlow = glowChoice === 'custom'
    ? customGlow
    : (GLOW_PRESETS.find(p => p.id === glowChoice)?.color ?? null);
  const setAndPersistGlow = (id) => {
    setGlowChoice(id);
    try { localStorage.setItem('wb_template_glow', id); } catch { /* ignore quota */ }
  };
  const setAndPersistCustom = (v) => {
    setCustomGlow(v);
    setGlowChoice('custom');
    try {
      localStorage.setItem('wb_template_glow_custom', v);
      localStorage.setItem('wb_template_glow', 'custom');
    } catch { /* ignore quota */ }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-templates">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white font-syne" data-testid="templates-title">Website Templates</h1>
          <p className="text-sm text-[#9CA3AF] mt-1 font-syne">Choose a template — blocks load into your Content Editor for full customization</p>
        </div>
        {/* Iter 189h — Glow color picker for template-card hover effect */}
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-xl"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(167,139,250,0.15)' }}
          data-testid="template-glow-picker"
        >
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50 mr-1">Hover glow</span>
          {GLOW_PRESETS.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => setAndPersistGlow(p.id)}
              className={`w-6 h-6 rounded-full transition-all ${glowChoice === p.id ? 'ring-2 ring-white/80 scale-110' : 'ring-1 ring-white/15 hover:ring-white/40'}`}
              style={{ background: p.swatch }}
              title={p.label}
              aria-label={`Glow: ${p.label}`}
              data-testid={`template-glow-${p.id}`}
            />
          ))}
          <label
            className={`w-6 h-6 rounded-full cursor-pointer relative overflow-hidden transition-all ${glowChoice === 'custom' ? 'ring-2 ring-white/80 scale-110' : 'ring-1 ring-white/15 hover:ring-white/40'}`}
            style={{ background: customGlow }}
            title="Custom color"
            data-testid="template-glow-custom-label"
          >
            <input
              type="color"
              value={customGlow}
              onChange={(e) => setAndPersistCustom(e.target.value)}
              className="absolute inset-0 opacity-0 cursor-pointer"
              data-testid="template-glow-custom-input"
            />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* Master-authored custom templates render alongside built-ins. */}
        {customTemplates.map((template) => {
          const glow = activeGlow || '#8B5CF6';
          return (
          <div
            key={template.id}
            className="template-card group relative bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl overflow-hidden transition-all duration-500"
            style={{ '--glow': glow }}
            data-testid={`template-${template.id}`}
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl -z-10"
              style={{ background: `radial-gradient(circle at 50% 40%, ${glow}55 0%, ${glow}00 65%)` }}
            />
            <div className="relative overflow-hidden bg-gradient-to-br from-[#1E1B4B] to-[#0E0E23]" style={{ aspectRatio: '16/10' }}>
              {template.thumbnail_url ? (
                <img src={template.thumbnail_url} alt={template.name} className="w-full h-full object-cover transform-gpu group-hover:scale-[1.04] transition-transform duration-[700ms] ease-out" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30 text-sm uppercase tracking-wider">Custom · {template.name}</div>
              )}
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                <button
                  onClick={() => openSwitchDialog({
                    id: template.id,
                    name: template.name,
                    category: template.category || 'Custom',
                    desc: template.desc || '',
                    accent: template.accent || '#8B5CF6',
                    blocks: (template.blocks || []).map(b => b.type),
                    // We don't have a full renderMockup for custom — use a static placeholder.
                    renderMockup: () => null,
                    renderFullPreview: () => null,
                  })}
                  disabled={applying === template.id || applied === template.id}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] transition-all font-syne disabled:opacity-50"
                  data-testid={`use-template-${template.id}`}
                >
                  {applying === template.id ? <Loader2 size={14} className="animate-spin" /> : applied === template.id ? <Check size={14} /> : <Sparkles size={14} />}
                  {applying === template.id ? 'Applying...' : applied === template.id ? 'Redirecting...' : 'Use Template'}
                </button>
              </div>
            </div>
            <div className="p-5">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium font-syne text-white/80 mb-2 inline-block" style={{ background: `${template.accent || '#8B5CF6'}30`, border: `1px solid ${template.accent || '#8B5CF6'}40` }}>{template.category || 'Custom'}</span>
              <h3 className="text-white font-bold font-syne text-lg" data-testid={`template-name-${template.id}`}>{template.name}</h3>
              <p className="text-[#9CA3AF] text-sm mt-1 font-syne leading-relaxed">{template.desc}</p>
            </div>
          </div>
          );
        })}

        {TEMPLATES.map((template) => {
          const glow = activeGlow || template.accent || '#8B5CF6';
          return (
          <div
            key={template.id}
            className="template-card group relative bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl overflow-hidden transition-all duration-500"
            style={{ '--glow': glow }}
            data-testid={`template-${template.id}`}
          >
            {/* Glow halo — pointer-events-none so it never intercepts clicks */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl -z-10"
              style={{ background: `radial-gradient(circle at 50% 40%, ${glow}55 0%, ${glow}00 65%)` }}
            />
            {/* Real mockup with stock photos — slow zoom on hover */}
            <div className="relative overflow-hidden" style={{ maxHeight: 280 }}>
              <div className="transform-gpu scale-100 group-hover:scale-[1.04] origin-top transition-transform duration-[700ms] ease-out">{template.renderMockup()}</div>
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#2D2D52]/90 to-transparent" />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                <button onClick={() => setPreviewTemplate(template)} className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-medium text-white bg-white/20 backdrop-blur hover:bg-white/30 transition-all font-syne" data-testid={`preview-template-${template.id}`}>
                  <Maximize2 size={14} /> Full Preview
                </button>
                <button onClick={() => openSwitchDialog(template)} disabled={applying === template.id || applied === template.id}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white transition-all font-syne disabled:opacity-50"
                  style={{ background: `linear-gradient(135deg, ${glow} 0%, ${glow}CC 100%)`, boxShadow: `0 0 25px ${glow}80` }}
                  data-testid={`use-template-${template.id}`}
                >
                  {applying === template.id ? <Loader2 size={14} className="animate-spin" /> : applied === template.id ? <Check size={14} /> : <Sparkles size={14} />}
                  {applying === template.id ? 'Applying...' : applied === template.id ? 'Redirecting...' : 'Use Template'}
                </button>
              </div>
            </div>
            {/* Info */}
            <div className="p-5">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium font-syne text-white/80 mb-2 inline-block" style={{ background: `${template.accent}30`, border: `1px solid ${template.accent}40` }}>{template.category}</span>
              <h3 className="text-white font-bold font-syne text-lg" data-testid={`template-name-${template.id}`}>{template.name}</h3>
              <p className="text-[#9CA3AF] text-sm mt-1 font-syne leading-relaxed">{template.desc}</p>
              <div className="flex flex-wrap gap-1 mt-3">
                {template.blocks.map((bt, i) => {
                  const Icon = blockIconMap[bt] || Layout;
                  return <span key={`${bt}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/5 text-[10px] text-[#9CA3AF] font-syne"><Icon size={8} />{blockLabelMap[bt]}</span>;
                })}
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* How Templates Work */}
      <div className="bg-[#2D2D52]/20 backdrop-blur-xl border border-white/5 rounded-2xl p-6 text-center" data-testid="templates-help">
        <h3 className="text-white font-syne font-bold mb-2">How Templates Work</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[#9CA3AF] text-sm font-syne">
          <div className="p-3"><div className="w-8 h-8 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center mx-auto mb-2 text-[#A78BFA] font-bold text-sm">1</div><p><strong className="text-white">Preview & choose</strong> — click "Full Preview" to see the template as a real website</p></div>
          <div className="p-3"><div className="w-8 h-8 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center mx-auto mb-2 text-[#A78BFA] font-bold text-sm">2</div><p><strong className="text-white">Blocks load into editor</strong> — drag to rearrange, click text to edit inline, upload your photos</p></div>
          <div className="p-3"><div className="w-8 h-8 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center mx-auto mb-2 text-[#A78BFA] font-bold text-sm">3</div><p><strong className="text-white">Make it yours</strong> — add or remove blocks, it's fully customizable</p></div>
        </div>
      </div>

      {/* Full-Page Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex flex-col" data-testid="template-full-preview-modal">
          <div className="flex items-center justify-between px-6 py-3 bg-[#12122A]/90 border-b border-white/10">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPreviewTemplate(null)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs tracking-wider uppercase text-white/80 hover:text-white border border-white/20 hover:border-white/40"
                data-testid="preview-back-btn"
                aria-label="Back to template list"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <span className="text-white font-syne font-bold">{previewTemplate.name}</span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium text-white/60" style={{ background: `${previewTemplate.accent}30` }}>{previewTemplate.category}</span>
            </div>
            <div className="flex items-center gap-3">
              {/* Device toggle */}
              <div className="flex bg-white/5 rounded-lg p-1 border border-white/10" data-testid="device-toggle">
                <button
                  onClick={() => setPreviewDevice('desktop')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-wider uppercase font-bold rounded-md transition-all ${previewDevice === 'desktop' ? 'bg-white text-[#0E0E23]' : 'text-white/60 hover:text-white'}`}
                  data-testid="preview-device-desktop"
                >
                  <Monitor size={13} /> Desktop
                </button>
                <button
                  onClick={() => setPreviewDevice('phone')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-wider uppercase font-bold rounded-md transition-all ${previewDevice === 'phone' ? 'bg-white text-[#0E0E23]' : 'text-white/60 hover:text-white'}`}
                  data-testid="preview-device-phone"
                >
                  <Smartphone size={13} /> Phone
                </button>
              </div>
              <button onClick={() => { const t = previewTemplate; setPreviewTemplate(null); openSwitchDialog(t); }}
                className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] hover:shadow-[0_0_20px_rgba(139,92,246,0.4)] font-syne" data-testid="preview-use-template-btn">
                <Sparkles size={14} /> Use This Template <ArrowRight size={14} />
              </button>
              <button onClick={() => setPreviewTemplate(null)} className="p-2 text-white/60 hover:text-white" data-testid="close-preview-modal"><XIcon size={20} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto flex justify-center" style={{ padding: previewDevice === 'phone' ? '32px 0' : '0', background: previewDevice === 'phone' ? '#1a1a2e' : 'transparent' }}>
            <style>{`
              .wb-phone-preview .grid { grid-template-columns: 1fr !important; gap: 16px !important; }
              .wb-phone-preview [class*="md:grid-cols-"],
              .wb-phone-preview [class*="sm:grid-cols-"],
              .wb-phone-preview [class*="grid-cols-"] { grid-template-columns: 1fr !important; }
              .wb-phone-preview img { max-width: 100% !important; height: auto !important; max-height: 280px !important; object-fit: cover !important; }
              .wb-phone-preview section { padding: 36px 18px !important; }
              .wb-phone-preview h1 { font-size: 2rem !important; line-height: 1.1 !important; }
              .wb-phone-preview h2 { font-size: 1.5rem !important; line-height: 1.15 !important; }
              .wb-phone-preview .text-7xl, .wb-phone-preview .text-8xl, .wb-phone-preview .text-6xl, .wb-phone-preview .text-5xl { font-size: 1.6rem !important; }
              .wb-phone-preview .text-4xl, .wb-phone-preview .text-3xl { font-size: 1.25rem !important; }
              .wb-phone-preview .text-2xl, .wb-phone-preview .text-xl, .wb-phone-preview .text-lg { font-size: 1rem !important; }
              .wb-phone-preview .columns-3, .wb-phone-preview .columns-2 { columns: 1 !important; }
              .wb-phone-preview [class*="max-w-"] { max-width: 100% !important; }
              .wb-phone-preview [class*="min-h-"] { min-height: 380px !important; }
            `}</style>
            {previewDevice === 'phone' ? (
              <div className="relative" style={{ width: 390, maxWidth: '100%' }} data-testid="preview-phone-frame">
                <div className="wb-phone-preview rounded-[40px] overflow-hidden shadow-2xl border-[10px] border-black bg-white" style={{ maxHeight: '85vh', overflowY: 'auto' }}>
                  <DashboardTemplatePreviewHeader template={previewTemplate} />
                  {previewTemplate.renderFullPreview()}
                  <DashboardTemplatePreviewFooter template={previewTemplate} />
                </div>
                <p className="text-center text-xs text-white/40 mt-4 tracking-widest">PHONE PREVIEW · 390 × auto</p>
              </div>
            ) : (
              <div className="max-w-[1280px] mx-auto bg-white min-h-full w-full" data-testid="preview-desktop-frame">
                <DashboardTemplatePreviewHeader template={previewTemplate} />
                {previewTemplate.renderFullPreview()}
                <DashboardTemplatePreviewFooter template={previewTemplate} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ───────── Switch-template confirmation ───────── */}
      {switchTpl && switchStage === 'pickMode' && (
        <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur flex items-center justify-center p-4" data-testid="switch-template-pick-mode">
          <div className="relative max-w-2xl w-full bg-gradient-to-br from-[#15152E] to-[#0E0E23] border border-white/[0.08] rounded-2xl p-7 shadow-[0_30px_80px_rgba(0,0,0,0.7)]">
            <button onClick={() => { setSwitchTpl(null); setSwitchStage(null); }} className="absolute top-4 right-4 text-white/55 hover:text-white p-1" data-testid="switch-close">
              <XIcon size={18} />
            </button>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#A78BFA] font-bold mb-2">Switch template</p>
            <h3 className="text-2xl font-bold text-white font-syne tracking-tight">
              Applying <span className="text-[#A78BFA]">{switchTpl.name}</span>
            </h3>
            <p className="text-sm text-white/55 mt-1.5">
              {currentBlocks > 0
                ? `You currently have ${currentBlocks} block${currentBlocks === 1 ? '' : 's'} on your homepage. How should we handle them?`
                : 'Your homepage is empty — this template will fill it in.'}
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              {/* Smart merge — only when content actually exists */}
              {currentBlocks > 0 && (
                <button
                  onClick={() => setSwitchMode('merge')}
                  className={`text-left rounded-xl p-5 border transition-all ${
                    switchMode === 'merge'
                      ? 'border-[#A78BFA] bg-[#A78BFA]/10 shadow-[0_0_24px_rgba(167,139,250,0.25)]'
                      : 'border-white/[0.08] bg-white/[0.025] hover:border-white/30 hover:bg-white/[0.05]'
                  }`}
                  data-testid="switch-mode-merge"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-violet-300">
                      <Sparkles size={11} /> Recommended
                    </span>
                    {switchMode === 'merge' && (
                      <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)' }}>
                        <Check size={11} className="text-white" strokeWidth={3} />
                      </span>
                    )}
                  </div>
                  <p className="text-lg font-bold text-white font-syne">Smart-merge</p>
                  <p className="text-xs text-white/65 mt-2 leading-relaxed">
                    Keep your text and images. We'll fit them into the new template's layout, colors, fonts, and section order. Best when you've already written content.
                  </p>
                </button>
              )}

              {/* Fresh start */}
              <button
                onClick={() => setSwitchMode('fresh')}
                className={`text-left rounded-xl p-5 border transition-all ${
                  switchMode === 'fresh'
                    ? 'border-amber-300 bg-amber-400/10 shadow-[0_0_24px_rgba(251,191,36,0.22)]'
                    : 'border-white/[0.08] bg-white/[0.025] hover:border-white/30 hover:bg-white/[0.05]'
                } ${currentBlocks === 0 ? 'sm:col-span-2' : ''}`}
                data-testid="switch-mode-fresh"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-amber-200">
                    <Layout size={11} /> Clean slate
                  </span>
                  {switchMode === 'fresh' && (
                    <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}>
                      <Check size={11} className="text-white" strokeWidth={3} />
                    </span>
                  )}
                </div>
                <p className="text-lg font-bold text-white font-syne">Fresh start</p>
                <p className="text-xs text-white/65 mt-2 leading-relaxed">
                  {currentBlocks > 0
                    ? `Replace all ${currentBlocks} blocks with the new template's placeholder content. Your edits will be lost.`
                    : "We'll fill your homepage with this template's beautiful default content for you to edit."}
                </p>
              </button>
            </div>

            <div className="flex items-center justify-between gap-3 mt-7">
              <button onClick={() => { setSwitchTpl(null); setSwitchStage(null); }} className="px-5 py-2.5 rounded-xl border border-white/15 text-white/75 hover:text-white hover:border-white/40 text-xs font-bold uppercase tracking-wider transition-all" data-testid="switch-cancel-pickmode">
                Cancel
              </button>
              <button
                onClick={() => setSwitchStage('finalConfirm')}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-wider shadow-[0_12px_30px_rgba(139,92,246,0.4)] hover:shadow-[0_12px_42px_rgba(139,92,246,0.55)] transition-all"
                data-testid="switch-next"
              >
                Continue <ArrowRight size={13} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Final confirm — irreversible action */}
      {switchTpl && switchStage === 'finalConfirm' && (
        <div className="fixed inset-0 z-[210] bg-black/85 backdrop-blur flex items-center justify-center p-4" data-testid="switch-template-final-confirm">
          <div className="relative max-w-md w-full bg-gradient-to-br from-[#15152E] to-[#0E0E23] border border-white/[0.08] rounded-2xl p-7 shadow-[0_30px_80px_rgba(0,0,0,0.8)]">
            <button onClick={() => setSwitchStage('pickMode')} className="absolute top-4 left-4 text-white/55 hover:text-white p-1 inline-flex items-center gap-1 text-xs" data-testid="switch-final-back">
              <ArrowLeft size={14} /> Back
            </button>
            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{
                background: switchMode === 'merge' ? 'linear-gradient(135deg, #8B5CF6, #6D28D9)' : 'linear-gradient(135deg, #F59E0B, #D97706)',
                boxShadow: switchMode === 'merge' ? '0 12px 30px -8px rgba(139,92,246,0.55)' : '0 12px 30px -8px rgba(245,158,11,0.55)',
              }}>
                {switchMode === 'merge' ? <Sparkles size={24} className="text-white" /> : <Layout size={24} className="text-white" />}
              </div>
              <h3 className="text-xl font-bold text-white font-syne">
                {switchMode === 'merge' ? 'Apply smart-merge?' : 'Replace everything?'}
              </h3>
              <p className="text-sm text-white/65 mt-2 leading-relaxed">
                {switchMode === 'merge'
                  ? <>We'll apply <span className="text-white font-bold">{switchTpl.name}</span> and keep your existing text/images where they fit. This action will overwrite your current homepage.</>
                  : <>We'll wipe your current homepage and replace all blocks with <span className="text-white font-bold">{switchTpl.name}</span>'s default content. <span className="text-amber-200 font-bold">This cannot be undone.</span></>
                }
              </p>
            </div>
            <div className="flex items-center justify-center gap-3 mt-6">
              <button onClick={() => { setSwitchTpl(null); setSwitchStage(null); }} className="px-5 py-2.5 rounded-xl border border-white/15 text-white/75 hover:text-white hover:border-white/40 text-xs font-bold uppercase tracking-wider" data-testid="switch-final-cancel">
                Cancel
              </button>
              <button
                onClick={() => { applyTemplate(switchTpl.id, switchMode); setSwitchTpl(null); }}
                disabled={applying === switchTpl.id}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-xs font-bold uppercase tracking-wider shadow-lg transition-all ${
                  switchMode === 'merge'
                    ? 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] hover:shadow-[0_12px_42px_rgba(139,92,246,0.55)]'
                    : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:shadow-[0_12px_42px_rgba(245,158,11,0.55)]'
                }`}
                data-testid="switch-final-confirm-btn"
              >
                {applying === switchTpl.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                Yes, apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Branded header rendered above every template preview — was missing entirely
 * which made the previews look unfinished. Uses the template's accent + name
 * so the brand placement is obvious.
 */
function DashboardTemplatePreviewHeader({ template }) {
  if (!template) return null;
  const accent = template.accent || '#7C3AED';
  const brand = (template.name || 'Your Brand').toUpperCase();
  return (
    <header
      className="w-full px-6 md:px-10 py-5 flex items-center justify-between border-b bg-white text-[#0E0E23]"
      style={{ borderColor: accent + '22' }}
      data-testid="preview-header"
    >
      <p className="text-xl md:text-2xl font-bold tracking-tight font-syne">{brand}</p>
      <nav className="hidden md:flex items-center gap-7 text-sm">
        {['Shop', 'About', 'Services', 'Contact'].map(l => (
          <span key={l} className="opacity-80 hover:opacity-100 cursor-pointer">{l}</span>
        ))}
        <span className="px-4 py-2 text-xs font-bold tracking-wider uppercase rounded text-white" style={{ background: accent }}>Book</span>
      </nav>
    </header>
  );
}

/**
 * Branded footer — 3-column standard (brand + socials, explore links,
 * newsletter). Mirrors what good production sites ship and was missing
 * from every template preview before this fix.
 */
function DashboardTemplatePreviewFooter({ template }) {
  if (!template) return null;
  const accent = template.accent || '#7C3AED';
  const brand = template.name || 'Your Brand';
  return (
    <footer
      className="w-full px-6 md:px-10 py-12 mt-2 bg-[#0E0E23] text-white"
      data-testid="preview-footer"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <p className="text-2xl font-bold font-syne">{brand}</p>
          <p className="mt-3 text-xs opacity-70 tracking-[0.2em] uppercase">Instagram · TikTok · Facebook</p>
        </div>
        <div>
          <p className="text-xs tracking-[0.18em] uppercase opacity-70 mb-3">Explore</p>
          <div className="space-y-1.5 text-sm opacity-90">
            <p>Home</p><p>About</p><p>Shop</p><p>Contact</p>
          </div>
        </div>
        <div>
          <p className="text-xs tracking-[0.18em] uppercase opacity-70 mb-3">Newsletter</p>
          <div className="flex gap-2">
            <span className="flex-1 rounded px-3 py-2 text-xs bg-white/10 border border-white/15 opacity-60">your@email.com</span>
            <span className="px-3 py-2 text-xs font-bold tracking-wider uppercase rounded" style={{ background: accent }}>Join</span>
          </div>
        </div>
      </div>
      <p className="mt-8 text-xs opacity-50 border-t border-white/15 pt-4 max-w-6xl mx-auto">
        © {new Date().getFullYear()} {brand}. Built on EntrepreNatives.
      </p>
    </footer>
  );
}
